// withHostnameOverlay — resolves the active brand variant per CHG-1263 §2.2
// formal precedence rule.
//
//   1. hostname_match   (highest precedence) — if hostname matches a brand's
//                                              domain list, return that
//   2. active_org_brand                       — else if activeOrg.brand_variant
//                                              is set, return that
//   3. platform_default                       — else fall back to "_default"
//
// Per CHG-1263 §2.3 "hostname overlay wins" doctrine: a user on a
// brand-variant hostname (e.g. my.mohseen.app) sees that brand's chrome
// regardless of which org they're currently in. This prevents the
// `feedback_brand_chrome_hostname_overlay.md` bug where org with
// brand_variant=NULL accessed via Mohseen domain rendered Alora chrome.
//
// Pure function — no React, no DOM, no Window. Testable in node.

import { detectBrand } from "../lib/runtime-base-urls";
import { variantIdFromBrand, type BrandVariantId } from "./brandChrome";

export interface ActiveOrgLike {
  id?: string;
  brand_variant?: BrandVariantId | null;
}

/**
 * Resolve the active brand variant per CHG-1263 §2.2.
 *
 * @param hostname  window.location.hostname at the call site
 * @param activeOrg currently-selected org, or null
 * @returns the resolved variant_id
 */
export function withHostnameOverlay(
  hostname: string,
  activeOrg: ActiveOrgLike | null = null,
): BrandVariantId {
  // Tier 1: hostname match wins.
  const detected = detectBrand(hostname);
  if (detected !== "unknown") {
    return variantIdFromBrand(detected);
  }
  // Tier 2: activeOrg.brand_variant fallback.
  if (activeOrg?.brand_variant) {
    return activeOrg.brand_variant;
  }
  // Tier 3: platform default.
  return "_default";
}
